import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as goalsActions from './goals.actions';
import { catchError, delay, map, mergeMap, reduce, take, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {
  Benchmark,
  ClassroomAnalytics,
  configurationSelectors,
  Facets,
  LevelLocation,
  Product,
  State,
  StudentAnalytics,
  WelGoal,
} from '@wam/shared';
import { GoalsService } from '../goals-data.service';
import { AssignmentsService } from '@app/shared/assignments.service';
import { of } from 'rxjs';
import { NotificationsService, NotificationType } from '@wam/notifications';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { AssessmentsService } from '@app/shared/services/assessments.service';
import { select, Store } from '@ngrx/store';
import * as fromGoals from './goals.selectors';
import { isEmpty, some } from 'lodash-es';
import { GoalSaveResultDialogComponent } from '@app/goals/goal-save-result-dialog/goal-save-result-dialog.component';
import { CourseAssignLicenseErrorDialogComponent } from '@app/shared/error-dialogs/course-assign-license-error-dialog/course-assign-license-error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AssignmentResponse, DaysOfWeek } from '@wap/catalog-v5';
import { Router } from '@angular/router';
import { transformFacetsToUrlParams } from '@app/shared/facets-transformers';
import { CurriculumVisibilityService } from '@app/shared/services/curriculum-visibility.service';
import { documentId } from '@app/shared/models/assignment.model';

@Injectable()
export class GoalsEffects {
  private actions$ = inject(Actions);
  private goalsService = inject(GoalsService);
  private assignmentsService = inject(AssignmentsService);
  private assessmentsService = inject(AssessmentsService);
  private curriculumVisibilityService = inject(CurriculumVisibilityService);
  private notificationsService = inject(NotificationsService);
  private analyticsService = inject(AnalyticsService);
  private store = inject<Store<State>>(Store);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.load, goalsActions.updateSelectedFacets),
      mergeMap(() =>
        this.goalsService.getHits().pipe(
          // @ts-ignore
          mergeMap((response: [ClassroomAnalytics[], number, Facets]) => [
            goalsActions.loadSuccess({ hits: response[0], totalHits: response[1] }),
            goalsActions.updateFacets({ facets: response[2] }),
          ]),
        ),
      ),
    ),
  );

  updateSelectedFacets$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.updateSelectedFacets),
        mergeMap(({ selectedFacets }) =>
          this.router.navigate([], {
            relativeTo: this.router.routerState.root,
            queryParams: transformFacetsToUrlParams(selectedFacets),
          }),
        ),
      ),
    { dispatch: false },
  );

  continueLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.continueLoad),
      mergeMap(() =>
        this.goalsService.getHits().pipe(
          // @ts-ignore
          mergeMap((response: [ClassroomAnalytics[], number, Facets]) => [
            goalsActions.continueLoadSuccess({ hits: response[0], totalHits: response[1] }),
            goalsActions.updateFacets({ facets: response[2] }),
          ]),
        ),
      ),
    ),
  );

  loadClassAssignments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.loadClassCourses),
        mergeMap(({ classUuid }) =>
          this.analyticsService.getStudentsInClass(classUuid).pipe(
            mergeMap((students: StudentAnalytics[]) => {
              this.store.dispatch(
                goalsActions.updateClassHasStudents({ classUuid, hasStudents: !isEmpty(students) }),
              );
              return students.map((student) =>
                this.assignmentsService.getCourses(student.organization.uuid, student.uuid),
              );
            }),
            mergeMap((assignments) => assignments),
            reduce((acc, val) => acc.concat(val), []),
            tap((courses: AssignmentResponse[]) =>
              this.store.dispatch(
                goalsActions.loadClassAssignmentsSuccess({ courses: { [classUuid]: courses } }),
              ),
            ),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  loadStudentAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadStudentCourses),
      mergeMap(({ student }) =>
        this.assignmentsService.getCourses(student.organization.uuid, student.uuid).pipe(
          map((courses: AssignmentResponse[]) =>
            goalsActions.loadStudentCoursesSuccess({
              student,
              courses,
            }),
          ),
        ),
      ),
    ),
  );

  loadStudentAssignmentsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.loadStudentCoursesSuccess),
        mergeMap(({ student, courses }) => {
          const erp = some(courses, (c) => c.document.product === Product.erp);
          const ems = some(courses, (c) => c.document.product === Product.ems);
          const smartstart = some(courses, (c) => c.document.product === Product.smartstart);
          this.store.dispatch(
            goalsActions.loadStudentLocation({
              organization: student.organization.uuid,
              entity: student.uuid,
              products: { erp, ems, smartstart },
            }),
          );
          return of(null);
        }),
      ),
    { dispatch: false },
  );

  studentAssignmentChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.studentCoursesChanged),
      mergeMap(({ student, courses }) =>
        this.goalsService.changeStudentAssignment(student, courses).pipe(
          mergeMap(() => [
            goalsActions.loadStudentCourses({ student }),
            goalsActions.studentCoursesChangedSuccess({ student, courses }),
          ]),
          catchError(({ failures }) => {
            const hasLicenseErrors = failures?.find((f) => f.isLicenseError);
            if (hasLicenseErrors) {
              this.showLicenseErrorDialog();
            } else {
              this.notificationsService.notify(
                NotificationType.ERROR,
                'Could not change student courses!',
              );
            }
            return of(
              goalsActions.loadStudentCourses({ student }),
              goalsActions.studentCoursesChangedFailed({ student, courses }),
            );
          }),
        ),
      ),
    ),
  );

  studentCoursesChangedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.studentCoursesChangedSuccess),
      delay(1000),
      map(() => goalsActions.load()),
    ),
  );

  classAssignmentChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.classCoursesChanged),
      mergeMap(({ classUuid, courses }) =>
        this.analyticsService.getStudentsInClass(classUuid).pipe(
          mergeMap((students: StudentAnalytics[]) =>
            this.goalsService.changeClassAssignment(students, courses).pipe(
              tap(({ failures, successes }) => {
                if (!isEmpty(failures) || !isEmpty(successes)) {
                  this.dialog.open(GoalSaveResultDialogComponent, {
                    width: '400px',
                    position: {
                      top: '50px',
                    },
                    data: {
                      students,
                      failures,
                      successes,
                    },
                    panelClass: 'no-padding-dialog',
                    autoFocus: 'h1',
                  });
                }
              }),
              map(() => goalsActions.loadClassCourses({ classUuid })),
              catchError(() => {
                this.notificationsService.notify(
                  NotificationType.ERROR,
                  'Could not change class courses!',
                );
                return of(goalsActions.loadClassCourses({ classUuid }));
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateLevel = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.updateLevel),
      map(() => goalsActions.load()),
    ),
  );

  // With CURRICULUM_VISIBILITY_SERVICE_ENABLED on, class/student visibility rules are stored in the
  // curriculum visibility service (one rule per course document) instead of the catalog assignments.
  courseVisibilityRulesChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.courseVisibilityRulesChanged),
      mergeMap(({ entity, forClass, courses }) =>
        this.curriculumVisibilityService
          .saveVisibilityRulesBatch(
            entity.organization.uuid,
            courses.map((course) => ({
              documentId: documentId(course.product),
              entity: entity.uuid,
              rules: {
                availableStartHour: course.availableStartHour ?? null,
                availableEndHour: course.availableEndHour ?? null,
                visibilityExcludedDays: (course.visibilityExcludedDays ?? []) as DaysOfWeek[],
              },
            })),
          )
          .pipe(
            map(() => goalsActions.courseVisibilityRulesChangedSuccess({ entity, forClass })),
            catchError(() => {
              this.notificationsService.notify(
                NotificationType.ERROR,
                'Could not change the visibility rules!',
              );
              return of(goalsActions.courseVisibilityRulesChangedFailed({ entity, forClass }));
            }),
          ),
      ),
    ),
  );

  // Reload the courses either way so the card reflects what is actually stored.
  courseVisibilityRulesChangedReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        goalsActions.courseVisibilityRulesChangedSuccess,
        goalsActions.courseVisibilityRulesChangedFailed,
      ),
      map(({ entity, forClass }) =>
        forClass
          ? goalsActions.loadClassCourses({ classUuid: entity.uuid })
          : goalsActions.loadStudentCourses({ student: entity as StudentAnalytics }),
      ),
    ),
  );

  loadGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadGoals),
      mergeMap(({ organization, entity }) =>
        this.goalsService.getGoals(organization, entity).pipe(
          map((goals: WelGoal[]) => goalsActions.loadGoalsSuccess({ goals: { [entity]: goals } })),
          catchError(() => {
            this.notificationsService.notify(NotificationType.ERROR, 'Could not load goals data.');
            return of(goalsActions.loadGoalsSuccess({ goals: { [entity]: [] } }));
          }),
        ),
      ),
    ),
  );

  goalChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.goalChanged),
      mergeMap(({ organization, entity, goal }) =>
        this.goalsService.saveGoal(organization, entity, goal).pipe(
          map(() => goalsActions.loadGoals({ organization, entity })),
          catchError((err) => {
            this.notificationsService.notify(
              NotificationType.ERROR,
              'The goal data you entered was invalid.',
            );
            return of(goalsActions.loadGoals({ organization, entity }));
          }),
        ),
      ),
    ),
  );

  loadTargets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadTargets),
      mergeMap(() =>
        this.goalsService
          .getTargets()
          .pipe(
            map(([erp, ems, smartstart]) =>
              goalsActions.loadTargetsSuccess({ targets: { erp, ems, smartstart } }),
            ),
          ),
      ),
    ),
  );

  loadOrgLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadOrgLocations),
      mergeMap(() =>
        this.goalsService.getOrgLocations().pipe(
          map(([erp, ems, smartstart]) => {
            const doNotChangeLevel = {
              id: -1,
              name: 'Do not change',
              locations: [{ id: -1, name: 'Do not change' }],
            };
            const doNotChangeLocation = { levelId: -1, locationId: -1 };
            erp.levels = [doNotChangeLevel, ...erp.levels];
            ems.levels = [doNotChangeLevel, ...ems.levels];
            smartstart.levels = [doNotChangeLevel, ...smartstart.levels];
            erp.location = doNotChangeLocation;
            ems.location = doNotChangeLocation;
            smartstart.location = doNotChangeLocation;
            return goalsActions.loadStudentLocationSuccess({
              entity: erp.organization,
              locations: { erp, ems, smartstart },
            });
          }),
        ),
      ),
    ),
  );

  loadStudentLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadStudentLocation),
      mergeMap(({ organization, entity, products }) =>
        this.goalsService.getStudentLocation(organization, entity, products).pipe(
          map(([erp, ems, smartstart]) =>
            goalsActions.loadStudentLocationSuccess({
              entity,
              locations: { erp, ems, smartstart },
            }),
          ),
        ),
      ),
    ),
  );

  loadLocationsForProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadLocationsForProduct),
      mergeMap(({ organization, entity, product }) => {
        const products = {
          erp: product === Product.erp,
          ems: product === Product.ems,
          smartstart: product === Product.smartstart,
        };
        return this.goalsService.getStudentLocation(organization, entity, products).pipe(
          map(([erp, ems, smartstart]) => {
            let locations;
            switch (product) {
              case Product.erp:
                locations = { erp };
                break;
              case Product.ems:
                locations = { ems };
                break;
              case Product.smartstart:
                locations = { smartstart };
                break;
            }
            return goalsActions.loadLocationsForProductSuccess({
              entity,
              locations,
            });
          }),
        );
      }),
    ),
  );

  studentLocationChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.studentLocationChanged),
        // @ts-ignore
        mergeMap(({ organization, entity, location }) =>
          this.store.pipe(
            select(fromGoals.getLocation),
            take(1),
            map((locations) => [locations[entity], location, organization, entity]),
          ),
        ),
        mergeMap(
          ([currentLocation, location, organization, entity]: [
            LevelLocation,
            LevelLocation,
            string,
            string,
          ]) => {
            const product = location.product;
            if (
              currentLocation[product]?.location.locationId !== location.locationId ||
              currentLocation[product]?.location.levelId !== location.levelId
            ) {
              return this.goalsService.saveLocation(organization, entity, location, 'Student').pipe(
                // @ts-ignore
                tap(() =>
                  this.store.dispatch(
                    goalsActions.loadLocationsForProduct({ organization, entity, product }),
                  ),
                ),
                map(({ error }) => {
                  if (error) {
                    this.notificationsService.notify(NotificationType.ERROR, error);
                  }
                }),
              );
            }
            return of(null);
          },
        ),
      ),
    { dispatch: false },
  );

  classLocationChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.classLocationChanged),
        mergeMap(({ organization, entity, location }) =>
          this.analyticsService.getStudentsInClass(entity).pipe(
            mergeMap((students: StudentAnalytics[]) =>
              this.goalsService.saveLocations(organization, students, location),
            ),
            tap((errors) =>
              errors.forEach((error) =>
                this.notificationsService.notify(NotificationType.ERROR, error, 10000),
              ),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadBenchmarks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadBenchmarks, goalsActions.loadBenchmarksFromStudentDialog),
      mergeMap(({ type, organization, entity }) =>
        this.store.select(configurationSelectors.getCourseConfiguration).pipe(
          take(1),
          map((course) => ({ type, organization, entity, aylaEnabled: course.ayla })),
        ),
      ),
      mergeMap(({ type, organization, entity, aylaEnabled }) =>
        this.assessmentsService.getBenchmarks(organization, entity).pipe(
          map((benchmarks: Benchmark[]) =>
            goalsActions.loadBenchmarksSuccess({
              benchmarks: { [entity]: benchmarks },
            }),
          ),
          catchError(() => {
            if (type !== goalsActions.loadBenchmarksFromStudentDialog.type && !aylaEnabled) {
              this.notificationsService.notify(
                NotificationType.WARNING,
                'Benchmarks are not yet available.',
              );
            }
            return of(goalsActions.loadBenchmarksSuccess({ benchmarks: { [entity]: [] } }));
          }),
        ),
      ),
    ),
  );

  benchmarkChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.benchmarkChanged),
      mergeMap(({ organization, entity, benchmark }) => {
        return this.assessmentsService.saveBenchmark(organization, entity, benchmark).pipe(
          map(() => goalsActions.loadBenchmarks({ organization, entity })),
          catchError(() => of(goalsActions.loadBenchmarks({ organization, entity }))),
        );
      }),
    ),
  );

  loadGradeGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadGradeGoals),
      mergeMap(({ organization, entity, grade, product }) =>
        this.goalsService.getGradeGoals(organization, entity, grade, product).pipe(
          map((goals) => goalsActions.loadGoalsSuccess({ goals })),
          catchError((error) => {
            this.notificationsService.notify(NotificationType.ERROR, 'Could not load goals data.');
            return of(goalsActions.loadGradeGoalsFailed({ error }));
          }),
        ),
      ),
    ),
  );

  saveGradeGoal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.saveGradeGoal),
      mergeMap(({ goal }) =>
        this.goalsService.saveGradeGoal(goal).pipe(
          map((response) => goalsActions.saveGradeGoalSuccess({ goal: response })),
          catchError((error) => of(goalsActions.saveGradeGoalFailed({ error }))),
        ),
      ),
    ),
  );

  saveGradeGoalSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.saveGradeGoalSuccess),
        tap(() =>
          this.notificationsService.notify(NotificationType.SUCCESS, 'Goal updated successfully.'),
        ),
      ),
    { dispatch: false },
  );

  saveGradeGoalFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.saveGradeGoalFailed),
        tap(() =>
          this.notificationsService.notify(
            NotificationType.ERROR,
            'An error occurred while trying to update goal.',
          ),
        ),
      ),
    { dispatch: false },
  );

  loadLock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.loadLock),
      mergeMap(({ organization, entity }) =>
        this.goalsService.getLock(organization, entity).pipe(
          map((lock: { [key: string]: boolean }) => goalsActions.loadLockSuccess({ lock })),
          catchError((error) => of(goalsActions.loadLockFailed({ error }))),
        ),
      ),
    ),
  );

  updateLock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goalsActions.updateLock),
      mergeMap(({ organization, entity, locked }) =>
        this.goalsService.updateLock(organization, entity, locked).pipe(
          map(() => goalsActions.updateLockSuccess()),
          catchError(() => of(goalsActions.updateLockFailed({ organization, entity, locked }))),
        ),
      ),
    ),
  );

  updateLockFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goalsActions.updateLockFailed),
        tap(() =>
          this.notificationsService.notify(
            NotificationType.ERROR,
            'An error occurred while trying to update goal lock.',
          ),
        ),
      ),
    { dispatch: false },
  );

  private showLicenseErrorDialog(): void {
    this.dialog.open(CourseAssignLicenseErrorDialogComponent, {
      width: '400px',
      panelClass: 'no-padding-dialog',
      autoFocus: 'h1',
    });
  }
}
